import { useLocation } from '@solidjs/router';
import { createContext, useContext, type JSX, createSignal, type Accessor, createEffect } from 'solid-js';
import {
  COMMUNICATIONS,
  HOME,
  MAINTENANCE,
  PAYMENTS,
  PAYMENT_METHODS,
  MAKE_PAYMENT,
  AUTO_PAYMENT_DETAIL,
  SET_UP_AUTO_PAYMENT,
  TRANSACTION_DETAILS,
  DEPOSITS,
  LEASE_OPTIONS,
} from '~/assets/strings';
import { type Icon, IconHome, IconWallet, IconWrench, IconMessage, IconLeaseOptions } from './icons';

export interface MenuItem {
  text: string;
  href: string;
  isShowBackButton?: boolean;
  isShowLeaseSelection?: boolean;
  icon?: Icon;
  position?: 'main' | 'bottom';
  children?: MenuItem[];
}

interface MenuContext {
  isShowLeaseSelection: Accessor<boolean>;
  collapsed: Accessor<boolean>;
  toggleCollapsed: () => void;
  isShowBackButton: Accessor<boolean>;
  menuItems: MenuItem[];
  pageTitle: Accessor<string>;
  isActive: (item: MenuItem) => boolean;
  onChangePageTitle: (title: string) => void;
  onHideNavbar: () => void;
  onShowNavbar: () => void;
}

const menuItems: MenuItem[] = [
  { text: HOME, href: '/tenants', icon: IconHome, position: 'main' },
  {
    text: DEPOSITS,
    href: '/tenants/deposits',
    icon: IconWallet,
    position: 'main',
    children: [
      { text: MAKE_PAYMENT, href: '/tenants/deposits/make-payment', isShowBackButton: true, isShowLeaseSelection: false },
      {
        text: TRANSACTION_DETAILS,
        href: '/tenants/deposits/transaction-detail',
        isShowBackButton: true,
        isShowLeaseSelection: false,
      },
    ],
  },
  {
    text: PAYMENTS,
    href: '/tenants/payments',
    icon: IconWallet,
    position: 'main',
    children: [
      { text: MAKE_PAYMENT, href: '/tenants/payments/make-payment', isShowBackButton: true, isShowLeaseSelection: false },
      {
        text: AUTO_PAYMENT_DETAIL,
        href: '/tenants/payments/auto-payment',
        isShowBackButton: true,
        isShowLeaseSelection: false,
      },
      {
        text: SET_UP_AUTO_PAYMENT,
        href: '/tenants/payments/auto-payment/set-up',
        isShowBackButton: true,
        isShowLeaseSelection: false,
      },
      {
        text: TRANSACTION_DETAILS,
        href: '/tenants/payments/transaction-detail',
        isShowBackButton: true,
        isShowLeaseSelection: false,
      },
    ],
  },
  { text: PAYMENT_METHODS, href: '/tenants/payment-methods', icon: IconWallet, position: 'main' },
  { text: MAINTENANCE, href: '/tenants/maintenance', icon: IconWrench, position: 'main' },
  { text: LEASE_OPTIONS, href: '/tenants/lease-options', icon: IconLeaseOptions, position: 'main' },
  { text: COMMUNICATIONS, href: '/tenants/chats', icon: IconMessage, position: 'main' },
  // { text: SERVICES, href: '/tenants/services', icon: IconServices, position: 'bottom' },
  // { text: SETTINGS, href: '/tenants/settings', icon: IconSettings, position: 'bottom' },
];

const flatMenusItems = menuItems.reduce((acc, cur) => [...acc, cur, ...(cur?.children ?? [])], [] as MenuItem[]);

const MenuContext = createContext<MenuContext>();

export const MenuProvider = (props: { children: JSX.Element }) => {
  const location = useLocation();
  const [activeItem, setActiveItem] = createSignal<MenuItem>();
  const [collapsed, setCollapsed] = createSignal<boolean>(true);
  const toggleCollapsed = () => setCollapsed((prev) => !prev);
  const [isShowBackButton, setIsShowBackButton] = createSignal(false);
  const [pageTitle, setPageTitle] = createSignal('');
  const [isShowLeaseSelection, setIsShowLeaseSelection] = createSignal(true);
  const isActive = (item: MenuItem) => item === activeItem();
  const context: MenuContext = {
    isShowLeaseSelection,
    collapsed,
    toggleCollapsed,
    menuItems,
    isActive,
    isShowBackButton,
    pageTitle,
    onChangePageTitle: (title: string) => setPageTitle(title),
    onHideNavbar: () => setIsShowLeaseSelection(false),
    onShowNavbar: () => setIsShowLeaseSelection(true),
  };

  createEffect(() => {
    const activeItem = flatMenusItems
      .slice()
      .reverse()
      .find((item: MenuItem) => location.pathname.startsWith(item.href));

    setPageTitle(activeItem?.text ?? '');
    setIsShowBackButton(activeItem?.isShowBackButton ?? false);
    setIsShowLeaseSelection(activeItem?.isShowLeaseSelection === false ? false : true);
    setActiveItem(activeItem);
  });

  return <MenuContext.Provider value={context}>{props.children}</MenuContext.Provider>;
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
